<template>
  <app-page title="My profile" icon="circle-user">
    <page-sub-header title="2 Factor Authentication" title-tag="h2" class="mb-3" />
    <sme-alert v-if="state.user.requires_2fa" level="good" class="my-3"> 2 Factor Authentication Enabled </sme-alert>

    <p v-if="state.user.phone">
      Phone number registered: <code>{{ state.user.phone }}</code
      >.<template v-if="!selfServicePhoneEnrolment"> If you wish to change it please contact support.</template>
    </p>
    <p v-else-if="!selfServicePhoneEnrolment" class="mb-0">
      We have no phone number for you configured yet, please contact support.
    </p>

    <!-- Self-service phone enrolment (feature-flagged). When off, behaviour is unchanged (contact support). -->
    <template v-if="selfServicePhoneEnrolment && (!state.user.phone || changingPhone) && !activating">
      <p class="mb-2">
        {{
          state.user.phone
            ? 'Enter a new mobile number to receive a verification code.'
            : 'Add a mobile number to enable SMS 2-factor authentication.'
        }}
      </p>
      <ValidationObserver v-if="!codeSent" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(sendCode)">
          <app-input
            v-model="phoneInput"
            type="text"
            name="mobile number"
            label="Mobile number"
            :placeholder="isUkCompany ? '+44 7700 900000' : '+1 555 123 4567'"
            :rules="{ required: true }"
            :disabled="sending"
            :validate-immediate="false"
          />
          <b-button type="submit" variant="primary" :disabled="v.invalid || sending">
            <b-spinner v-if="sending" class="mr-2" small />
            {{ sending ? 'Sending...' : 'Send code' }}
          </b-button>
          <b-button v-if="changingPhone" variant="link" class="ml-2" :disabled="sending" @click="cancelChangePhone">
            Cancel
          </b-button>
        </b-form>
      </ValidationObserver>

      <ValidationObserver v-else :key="`verify-${phoneInput}`" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(verifyCode)">
          <p>
            We have sent a <b>6 digit code</b> by SMS to <code>{{ phoneInput }}</code
            >. Enter it below to verify your number.
          </p>
          <app-input
            v-model="verificationCode"
            type="text"
            name="verification code"
            label="Verification code"
            placeholder="6 digit code"
            :rules="{ required: true, regex: /^\d{6}$/ }"
            :disabled="verifying"
            :validate-immediate="false"
          />
          <b-button type="submit" variant="primary" :disabled="v.invalid || verifying">
            <b-spinner v-if="verifying" class="mr-2" small />
            {{ verifying ? 'Verifying...' : 'Verify' }}
          </b-button>
          <b-button variant="link" class="ml-2" :disabled="verifying" @click="resetToSendCode">
            Use a different number
          </b-button>
        </b-form>
      </ValidationObserver>
    </template>

    <b-button
      v-if="selfServicePhoneEnrolment && state.user.phone && !changingPhone && !activating"
      variant="link"
      class="px-0"
      @click="startChangePhone"
    >
      Change phone number
    </b-button>

    <template v-if="!state.user.requires_2fa && state.user.phone && !activating && !changingPhone">
      <b-button variant="primary" @click="activate"> Enable 2 Factor Authentication </b-button>
    </template>

    <template v-if="activating">
      <app-loading :loading="!challenge" />
      <template v-if="challenge">
        <p v-if="state.user.phone">
          We have just sent you a <b>6 digit pin</b> to you phone. Please fill it bellow and submit.
        </p>
        <ValidationObserver v-slot="v">
          <b-form @submit.prevent="v.handleSubmit(onSubmit)">
            <app-input
              v-model="challengePin"
              type="text"
              name="challenge pin"
              placeholder="6 digit pin"
              label="2FA Pin"
              description="Check your phone SMS for a 2FA pin"
              :rules="{ required: true, regex: /^\d{6}$/ }"
              :disabled="challenging"
              :validate-immediate="false"
            />
            <page-sub-footer>
              <template #before>
                <sme-alert v-if="error" level="danger">{{ error }}</sme-alert>
              </template>
              <b-button type="submit" variant="primary" :disabled="v.invalid || challenging">
                <b-spinner v-if="challenging" class="mr-2" small />
                {{ challenging ? 'Submitting...' : 'Submit' }}
              </b-button>
            </page-sub-footer>
          </b-form>
        </ValidationObserver>
      </template>
    </template>

    <sme-alert v-if="error && !activating" level="danger" class="mt-3">{{ error }}</sme-alert>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';

export default {
  name: 'Profile',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, PageSubHeader, SmeAlert },
  setup() {
    const { selfServicePhoneEnrolment, isUkCompany } = useFeatureFlags();
    return { selfServicePhoneEnrolment, isUkCompany };
  },
  data() {
    return {
      challenge: undefined,
      challengePin: undefined,
      error: undefined,
      activating: false,
      challenging: false,
      // self-service phone enrolment
      phoneInput: undefined,
      verificationCode: undefined,
      codeSent: false,
      changingPhone: false,
      sending: false,
      verifying: false,
      state: State.state,
    };
  },
  methods: {
    startChangePhone() {
      this.changingPhone = true;
      this.codeSent = false;
      this.phoneInput = undefined;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    cancelChangePhone() {
      this.changingPhone = false;
      this.codeSent = false;
      this.phoneInput = undefined;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    resetToSendCode() {
      this.codeSent = false;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    async sendCode() {
      this.sending = true;
      this.error = undefined;
      try {
        await ApiClient.sendPhoneVerification(this.phoneInput, this.state.user.user_id);
        this.codeSent = true;
      } catch (error) {
        this.error =
          error.variables?.message || error.message || 'Could not send a verification code. Please try again.';
      } finally {
        this.sending = false;
      }
    },
    async verifyCode() {
      this.verifying = true;
      this.error = undefined;
      try {
        await ApiClient.verifyPhone(this.state.user.user_id, this.phoneInput, this.verificationCode);
        await ApiClient.getUser();
        this.codeSent = false;
        this.changingPhone = false;
        this.$appToast('Phone number verified', { title: '2 Factor Authentication', variant: 'success' });
      } catch (error) {
        this.error =
          error.variables?.message || error.message || 'Verification failed. Please check the code and try again.';
      } finally {
        this.verifying = false;
      }
    },
    activate() {
      this.activating = true;
      ApiClient.createChallenge2fa(this.state.user.user_id).then(
        challenge => {
          this.challenge = challenge;
        },
        error => {
          this.error = error;
        },
      );
    },
    onSubmit() {
      this.challenging = true;
      ApiClient.checkChallenge2fa(this.challenge.mfa_id, this.challengePin).then(
        response => {
          if (response.is_valid) {
            const newUser = {
              user_id: this.state.user.user_id,
              requires_2fa: true,
            };
            ApiClient.patchUser(newUser, this.state.company.company_id).then(
              () => {
                this.$appToast(`You'll need to enter a valid PIN on each login`, {
                  title: 'Enable 2 Factor Authentication',
                  variant: 'success',
                });
                ApiClient.getUser().then(
                  () => {
                    this.activating = false;
                    this.challenging = false;
                  },
                  error => {
                    this.error = error.message;
                    this.challenging = false;
                  },
                );
              },
              error => {
                this.error = error.message;
                this.challenging = false;
              },
            );
          } else {
            this.error = 'Invalid Pin submitted';
            this.challenging = false;
          }
        },
        error => {
          this.challenging = false;
          this.error = error.message;
        },
      );
    },
  },
};
</script>
